import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  makeStyles,
  MenuItem,
  IconButton,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Menu,
  Tooltip,
} from "@material-ui/core";
import AnchorIcon from "@mui/icons-material/Anchor";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    width: "100%",
    height: "100%",
    gap: "8px",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  priceListColumn: {
    flex: "0 0 auto",
    width: "80px",
  },
  headerSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 12px",
    fontSize: "11px",
    fontWeight: "bold",
    color: theme.palette.text.primary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderRadius: "8px 8px 0 0",
    minHeight: "40px",
    position: "relative",
    zIndex: 10, // Ensure headers are above scrollable content
  },
  bodySection: {
    flex: 1,
    borderRadius: "0 0 8px 8px",
    borderTop: "none",
  },
  sectionLabel: {
    fontSize: "12px",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  sectionControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  iconGroup: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "4px",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
}));

const BackgroundGrid = ({ config, controlProps }) => {
  // Extract client color first
  const userColor = config.visual?.colors?.[0] || "#3498db";
  const clientColor =
    controlProps?.clientColor || config.userImplementationColor || userColor;

  const classes = useStyles();
  const { isSkeletonMode, gridTemplate } = config;

  const [displayModeAnchorEl, setDisplayModeAnchorEl] = useState(null);
  const [sellinDisplayModeAnchorEl, setSellinDisplayModeAnchorEl] =
    useState(null);
  const [relativityMenuAnchorEl, setRelativityMenuAnchorEl] = useState(null);

  // Fixed styling colors for section headers and background
  const sectionStyle = {
    backgroundColor: "rgba(100, 116, 139, 0.12)",
    borderTop: "1px solid rgba(100, 116, 139, 0.25)",
    borderRight: "1px solid rgba(100, 116, 139, 0.25)",
    borderBottom: "1px solid rgba(100, 116, 139, 0.25)",
    borderLeft: "1px solid rgba(100, 116, 139, 0.25)",
  };

  // Control props with fallbacks
  const {
    availableAnchors = [],
    selectedAnchor = "",
    onAnchorChange = () => {},
    displayModes = [],
    onDisplayModeChange = () => {},
    sellinDisplayModes = [],
    onSellinDisplayModeChange = () => {},
    showPriceList = true,
    onShowPriceListChange = () => {},
  } = controlProps || {};

  // Dynamically adjust grid template based on Price List visibility
  const dynamicGridColumns = showPriceList
    ? gridTemplate?.columns || "80px 1fr 140px 1fr 1fr 1fr"
    : "1fr 140px 1fr 1fr 1fr"; // Remove 80px Price List column when hidden

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: dynamicGridColumns,
    gridTemplateRows: "40px 1fr",
    gap: "0 8px",
    width: "100%",
    height: "100%",
  };

  // Adjust column spans based on Price List visibility
  const sellinHeaderColumn = showPriceList ? "1 / 3" : "1 / 2";
  const channelsHeaderColumn = showPriceList ? "3 / 6" : "2 / 5";
  const selloutHeaderColumn = showPriceList ? 6 : 5;

  return (
    <div style={gridStyle}>
      {/* Sellin Header */}
      <div
        className={classes.headerSection}
        style={{
          ...sectionStyle,
          gridColumn: sellinHeaderColumn,
          gridRow: 1,
          borderBottom: "none",
        }}
      >
        <span className={classes.sectionLabel}>Sellin</span>
        {!isSkeletonMode && (
          <div className={classes.sectionControls}>
            <Tooltip title="Toggle Visibility">
              <div
                onClick={(e) => setSellinDisplayModeAnchorEl(e.currentTarget)}
                className={classes.iconGroup}
              >
                <IconButton size="small" style={{ padding: "4px" }}>
                  <FontAwesomeIcon icon={faEye} size="sm" />
                </IconButton>
                <span
                  style={{
                    fontSize: "11px",
                    userSelect: "none",
                    color: "rgba(0, 0, 0, 0.54)",
                  }}
                >
                  ({sellinDisplayModes.length}/3)
                </span>
                <KeyboardArrowDownIcon style={{ fontSize: "14px" }} />
              </div>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Sellin Body Background */}
      <div
        style={{
          ...sectionStyle,
          gridColumn: sellinHeaderColumn,
          gridRow: 2,
          borderRadius: "0 0 8px 8px",
          borderBottom: "none",
        }}
      />

      {/* Channels Header */}
      <div
        className={classes.headerSection}
        style={{
          ...sectionStyle,
          gridColumn: channelsHeaderColumn,
          gridRow: 1,
          borderBottom: "none",
        }}
      >
        <span className={classes.sectionLabel}>Channels</span>
        {!isSkeletonMode && (
          <div className={classes.sectionControls}>
            <Tooltip title="Toggle Visibility">
              <div
                onClick={(e) => setDisplayModeAnchorEl(e.currentTarget)}
                className={classes.iconGroup}
              >
                <IconButton size="small" style={{ padding: "4px" }}>
                  <FontAwesomeIcon icon={faEye} size="sm" />
                </IconButton>
                <span
                  style={{
                    fontSize: "11px",
                    userSelect: "none",
                    color: "rgba(0, 0, 0, 0.54)",
                  }}
                >
                  ({displayModes.length}/3)
                </span>
                <KeyboardArrowDownIcon style={{ fontSize: "14px" }} />
              </div>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Channels Body Background */}
      <div
        style={{
          ...sectionStyle,
          gridColumn: channelsHeaderColumn,
          gridRow: 2,
          borderRadius: "0 0 8px 8px",
          borderBottom: "none",
        }}
      />

      {/* Sellout Header */}
      <div
        className={classes.headerSection}
        style={{
          ...sectionStyle,
          gridColumn: selloutHeaderColumn,
          gridRow: 1,
          borderBottom: "none",
        }}
      >
        <span className={classes.sectionLabel}>Sellout</span>
        {!isSkeletonMode && (
          <div className={classes.sectionControls}>
            <Tooltip title="Anchor Relativity">
              <div
                onClick={(e) => setRelativityMenuAnchorEl(e.currentTarget)}
                className={classes.iconGroup}
              >
                <IconButton size="small" style={{ padding: "4px" }}>
                  <AnchorIcon style={{ fontSize: "16px" }} />
                </IconButton>
                <span
                  style={{
                    fontSize: "11px",
                    textTransform: "capitalize",
                    userSelect: "none",
                    color: "rgba(0, 0, 0, 0.54)",
                  }}
                >
                  Relativity
                </span>
                <KeyboardArrowDownIcon style={{ fontSize: "14px" }} />
              </div>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Sellout Body Background */}
      <div
        style={{
          ...sectionStyle,
          gridColumn: selloutHeaderColumn,
          gridRow: 2,
          borderRadius: "0 0 8px 8px",
          borderBottom: "none",
        }}
      />

      {/* Relativity Menu (for Sellout icon) */}
      {!isSkeletonMode && (
        <Menu
          anchorEl={relativityMenuAnchorEl}
          open={Boolean(relativityMenuAnchorEl)}
          onClose={() => setRelativityMenuAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            style: {
              boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.3)",
            },
          }}
        >
          {availableAnchors.map((anchor) => (
            <MenuItem
              key={anchor.id}
              onClick={() => {
                onAnchorChange(anchor.id);
                setRelativityMenuAnchorEl(null);
              }}
              selected={selectedAnchor === anchor.id}
            >
              {anchor.label}
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Sellin Display Mode Popover */}
      {!isSkeletonMode && (
        <Popover
          open={Boolean(sellinDisplayModeAnchorEl)}
          anchorEl={sellinDisplayModeAnchorEl}
          onClose={() => setSellinDisplayModeAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            style: {
              boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.3)",
            },
          }}
        >
          <div style={{ padding: "16px", minWidth: "350px" }}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "12px",
              }}
            >
              Show Sellin Info
            </Typography>

            {/* Price List Toggle - Separate section at top */}
            <div
              style={{
                marginBottom: "16px",
                paddingBottom: "12px",
                borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
              }}
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showPriceList}
                      onChange={(e) => onShowPriceListChange(e.target.checked)}
                      style={{
                        color: showPriceList ? clientColor : undefined,
                      }}
                    />
                  }
                  label={
                    <span style={{ fontSize: "12px", fontWeight: "600" }}>
                      Show Price List Column
                    </span>
                  }
                />
              </FormGroup>
            </div>

            {/* Tabular structure with three columns */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "16px",
              }}
            >
              {/* Discount Column */}
              <div>
                <Typography
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: clientColor,
                    marginBottom: "8px",
                    padding: "4px 8px",
                    backgroundColor: `${clientColor}15`,
                    borderRadius: "4px",
                    textAlign: "center",
                  }}
                >
                  Discount
                </Typography>
                <FormGroup style={{ paddingLeft: "8px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={sellinDisplayModes.includes("discount")}
                        disabled={
                          !sellinDisplayModes.includes("discount") &&
                          sellinDisplayModes.length >= 3
                        }
                        onChange={(e) => {
                          const newModes = e.target.checked
                            ? [...sellinDisplayModes, "discount"]
                            : sellinDisplayModes.filter(
                                (mode) => mode !== "discount",
                              );
                          if (newModes.length > 0 && newModes.length <= 3)
                            onSellinDisplayModeChange(newModes);
                        }}
                        style={{
                          color: sellinDisplayModes.includes("discount")
                            ? clientColor
                            : undefined,
                        }}
                      />
                    }
                    label={<span style={{ fontSize: "11px" }}>Discount</span>}
                  />
                </FormGroup>
              </div>

              {/* Taxes Column */}
              <div>
                <Typography
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: clientColor,
                    marginBottom: "8px",
                    padding: "4px 8px",
                    backgroundColor: `${clientColor}15`,
                    borderRadius: "4px",
                    textAlign: "center",
                  }}
                >
                  Taxes
                </Typography>
                <FormGroup style={{ paddingLeft: "8px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={sellinDisplayModes.includes("taxes")}
                        disabled={
                          !sellinDisplayModes.includes("taxes") &&
                          sellinDisplayModes.length >= 3
                        }
                        onChange={(e) => {
                          const newModes = e.target.checked
                            ? [...sellinDisplayModes, "taxes"]
                            : sellinDisplayModes.filter(
                                (mode) => mode !== "taxes",
                              );
                          if (newModes.length > 0 && newModes.length <= 3)
                            onSellinDisplayModeChange(newModes);
                        }}
                        style={{
                          color: sellinDisplayModes.includes("taxes")
                            ? clientColor
                            : undefined,
                        }}
                      />
                    }
                    label={<span style={{ fontSize: "11px" }}>Taxes</span>}
                  />
                </FormGroup>
              </div>

              {/* Price Column */}
              <div>
                <Typography
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: clientColor,
                    marginBottom: "8px",
                    padding: "4px 8px",
                    backgroundColor: `${clientColor}15`,
                    borderRadius: "4px",
                    textAlign: "center",
                  }}
                >
                  Price
                </Typography>
                <FormGroup style={{ paddingLeft: "8px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={sellinDisplayModes.includes("priceNet")}
                        disabled={
                          !sellinDisplayModes.includes("priceNet") &&
                          sellinDisplayModes.length >= 3
                        }
                        onChange={(e) => {
                          const newModes = e.target.checked
                            ? [...sellinDisplayModes, "priceNet"]
                            : sellinDisplayModes.filter(
                                (mode) => mode !== "priceNet",
                              );
                          if (newModes.length > 0 && newModes.length <= 3)
                            onSellinDisplayModeChange(newModes);
                        }}
                        style={{
                          color: sellinDisplayModes.includes("priceNet")
                            ? clientColor
                            : undefined,
                        }}
                      />
                    }
                    label={<span style={{ fontSize: "11px" }}>Price Net</span>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={sellinDisplayModes.includes("priceGross")}
                        disabled={
                          !sellinDisplayModes.includes("priceGross") &&
                          sellinDisplayModes.length >= 3
                        }
                        onChange={(e) => {
                          const newModes = e.target.checked
                            ? [...sellinDisplayModes, "priceGross"]
                            : sellinDisplayModes.filter(
                                (mode) => mode !== "priceGross",
                              );
                          if (newModes.length > 0 && newModes.length <= 3)
                            onSellinDisplayModeChange(newModes);
                        }}
                        style={{
                          color: sellinDisplayModes.includes("priceGross")
                            ? clientColor
                            : undefined,
                        }}
                      />
                    }
                    label={
                      <span style={{ fontSize: "11px" }}>Price Gross</span>
                    }
                  />
                </FormGroup>
              </div>
            </div>
          </div>
        </Popover>
      )}

      {/* Display Mode Popover (for Channels eye icon) */}
      {!isSkeletonMode && (
        <Popover
          open={Boolean(displayModeAnchorEl)}
          anchorEl={displayModeAnchorEl}
          onClose={() => setDisplayModeAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            style: {
              boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.3)",
            },
          }}
        >
          <div style={{ padding: "16px", minWidth: "350px" }}>
            <Typography
              style={{
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "12px",
              }}
            >
              Show Channel Info
            </Typography>
            {/* Tabular structure with three columns */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "16px",
              }}
            >
              {/* Margin Column */}
              <div>
                <Typography
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: clientColor,
                    marginBottom: "8px",
                    padding: "4px 8px",
                    backgroundColor: `${clientColor}15`,
                    borderRadius: "4px",
                    textAlign: "center",
                  }}
                >
                  Margin
                </Typography>
                <FormGroup style={{ paddingLeft: "8px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={displayModes.includes("margin")}
                        disabled={
                          !displayModes.includes("margin") &&
                          displayModes.length >= 3
                        }
                        onChange={(e) => {
                          const newModes = e.target.checked
                            ? [...displayModes, "margin"]
                            : displayModes.filter((mode) => mode !== "margin");
                          if (newModes.length > 0 && newModes.length <= 3)
                            onDisplayModeChange(newModes);
                        }}
                        style={{
                          color: displayModes.includes("margin")
                            ? clientColor
                            : undefined,
                        }}
                      />
                    }
                    label={
                      <span style={{ fontSize: "11px" }}>Margin Front</span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={displayModes.includes("marginTotal")}
                        disabled={
                          !displayModes.includes("marginTotal") &&
                          displayModes.length >= 3
                        }
                        onChange={(e) => {
                          const newModes = e.target.checked
                            ? [...displayModes, "marginTotal"]
                            : displayModes.filter(
                                (mode) => mode !== "marginTotal",
                              );
                          if (newModes.length > 0 && newModes.length <= 3)
                            onDisplayModeChange(newModes);
                        }}
                        style={{
                          color: displayModes.includes("marginTotal")
                            ? clientColor
                            : undefined,
                        }}
                      />
                    }
                    label={
                      <span style={{ fontSize: "11px" }}>Margin Total</span>
                    }
                  />
                </FormGroup>
              </div>

              {/* Taxes Column */}
              <div>
                <Typography
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: clientColor,
                    marginBottom: "8px",
                    padding: "4px 8px",
                    backgroundColor: `${clientColor}15`,
                    borderRadius: "4px",
                    textAlign: "center",
                  }}
                >
                  Taxes
                </Typography>
                <FormGroup style={{ paddingLeft: "8px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={displayModes.includes("taxes")}
                        disabled={
                          !displayModes.includes("taxes") &&
                          displayModes.length >= 3
                        }
                        onChange={(e) => {
                          const newModes = e.target.checked
                            ? [...displayModes, "taxes"]
                            : displayModes.filter((mode) => mode !== "taxes");
                          if (newModes.length > 0 && newModes.length <= 3)
                            onDisplayModeChange(newModes);
                        }}
                        style={{
                          color: displayModes.includes("taxes")
                            ? clientColor
                            : undefined,
                        }}
                      />
                    }
                    label={<span style={{ fontSize: "11px" }}>Taxes</span>}
                  />
                </FormGroup>
              </div>

              {/* Price Column */}
              <div>
                <Typography
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: clientColor,
                    marginBottom: "8px",
                    padding: "4px 8px",
                    backgroundColor: `${clientColor}15`,
                    borderRadius: "4px",
                    textAlign: "center",
                  }}
                >
                  Price
                </Typography>
                <FormGroup style={{ paddingLeft: "8px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={displayModes.includes("priceNet")}
                        disabled={
                          !displayModes.includes("priceNet") &&
                          displayModes.length >= 3
                        }
                        onChange={(e) => {
                          const newModes = e.target.checked
                            ? [...displayModes, "priceNet"]
                            : displayModes.filter(
                                (mode) => mode !== "priceNet",
                              );
                          if (newModes.length > 0 && newModes.length <= 3)
                            onDisplayModeChange(newModes);
                        }}
                        style={{
                          color: displayModes.includes("priceNet")
                            ? clientColor
                            : undefined,
                        }}
                      />
                    }
                    label={<span style={{ fontSize: "11px" }}>Price Net</span>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={displayModes.includes("priceGross")}
                        disabled={
                          !displayModes.includes("priceGross") &&
                          displayModes.length >= 3
                        }
                        onChange={(e) => {
                          const newModes = e.target.checked
                            ? [...displayModes, "priceGross"]
                            : displayModes.filter(
                                (mode) => mode !== "priceGross",
                              );
                          if (newModes.length > 0 && newModes.length <= 3)
                            onDisplayModeChange(newModes);
                        }}
                        style={{
                          color: displayModes.includes("priceGross")
                            ? clientColor
                            : undefined,
                        }}
                      />
                    }
                    label={
                      <span style={{ fontSize: "11px" }}>Price Gross</span>
                    }
                  />
                </FormGroup>
              </div>
            </div>
          </div>
        </Popover>
      )}
    </div>
  );
};

BackgroundGrid.propTypes = {
  config: PropTypes.shape({
    visual: PropTypes.shape({
      colors: PropTypes.arrayOf(PropTypes.string),
      scrollableContentHeight: PropTypes.number, // Height for PriceListBox matching
    }),
    isSkeletonMode: PropTypes.bool,
    userImplementationColor: PropTypes.string, // Used via config.userImplementationColor for fallback color
    gridTemplate: PropTypes.shape({
      columns: PropTypes.string,
      rows: PropTypes.string,
    }),
  }).isRequired,
  controlProps: PropTypes.shape({
    availableAnchors: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
    selectedAnchor: PropTypes.string,
    onAnchorChange: PropTypes.func,
    displayModes: PropTypes.arrayOf(PropTypes.string),
    onDisplayModeChange: PropTypes.func,
    clientColor: PropTypes.string,
  }),
};

export default BackgroundGrid;
